<template>
  <model-table
    :collection="boxOfficeReportSettlements"
    :columns="columns"
    :show-add="false"
    :show-edit="false"
    :submodel="submodel"
    :initial-selection="initialSelection"
    :actions="[
      {
        label: 'Settle',
        icon: 'done_all',
        disabled: (boxOfficeReportSettlement) => !$_.includes([STATES.PAID, STATES.VOIDED], boxOfficeReportSettlement.state),
        disabledTooltip: (boxOfficeReportSettlement) => !$_.includes([STATES.PAID, STATES.VOIDED], boxOfficeReportSettlement.state)
          ? 'This settlement must be paid or voided to be settled.'
          : '',
        handler: (boxOfficeReportSettlement) => boxOfficeReportSettlement.settle()
      },
      {
        label: 'Unsettle',
        icon: 'remove_done',
        disabled: (boxOfficeReportSettlement) => !$_.includes([STATES.FINALIZED, STATES.VOIDED], boxOfficeReportSettlement.state),
        disabledTooltip: (boxOfficeReportSettlement) => !$_.includes([STATES.FINALIZED, STATES.VOIDED], boxOfficeReportSettlement.state)
          ? 'This settlement must be finalized or voided to be unsettled.'
          : '',
        handler: (boxOfficeReportSettlement) => boxOfficeReportSettlement.unsettle()
      },
      {
        label: 'Void',
        icon: 'cancel',
        disabled: (boxOfficeReportSettlement) => !$_.includes([STATES.OPEN, STATES.PROCESSING], boxOfficeReportSettlement.state),
        disabledTooltip: (boxOfficeReportSettlement) => !$_.includes([STATES.OPEN, STATES.PROCESSING], boxOfficeReportSettlement.state)
          ? 'This boxOfficeReportSettlement must be not be open or processing to be voided.'
          : '',
        handler: (boxOfficeReportSettlement) => boxOfficeReportSettlement.void()
      }
    ]"
  >
    <template #header-controls>
      <div class="row q-mt-xs q-gutter-xs">
        <box-office-report-settlement-filter
          ref="boxOfficeReportSettlementFilter"
          :filters="filters"
          :studios="studios.data"
          :movies="movies.data"
          :movie-terms="movieTerms.data"
        />
      </div>
    </template>
    <template
      v-if="showControls"
      #header-buttons
    >
      <q-btn
        label="Batch"
        outline
        color="primary"
        :disable="!allowBatch"
        @click.stop="$refs.batchDialog.show()"
      >
        <q-tooltip>
          {{ allowBatch ? 'Batch' : 'Save Movie Adjustments to generate settlements before batching' }}
        </q-tooltip>
      </q-btn>
      <q-btn-dropdown
        label="Update"
        auto-close
        outline
        padding="sm"
        :disable="$_.isNil(boxOfficeReportSettlement?.data?.length) || boxOfficeReportSettlement?.data?.length === 0"
      >
        <q-list>
          <q-item
            v-close-popup
            clickable
            @click="settleAll"
          >
            <q-item-section>
              <q-item-label>Settle ({{ boxOfficeReportSettlement?.data?.length || 0 }})</q-item-label>
            </q-item-section>
          </q-item>
          <q-item
            v-close-popup
            clickable
            @click="unsettleAll"
          >
            <q-item-section>
              <q-item-label>Unsettle ({{ boxOfficeReportSettlement?.data?.length || 0 }})</q-item-label>
            </q-item-section>
          </q-item>
          <q-item
            v-close-popup
            clickable
            @click="voidAll"
          >
            <q-item-section>
              <q-item-label>Void ({{ boxOfficeReportSettlement?.data?.length || 0 }})</q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
      </q-btn-dropdown>
    </template>
    <box-office-report-settlement-batch-dialog
      ref="batchDialog"
      :filters="filters"
      :box-office-report-settlements="boxOfficeReportSettlements"
      :studios="studios.data"
      :movies="movies.data"
      :movie-terms="movieTerms.data"
    />
  </model-table>
</template>

<script>

import { iconForState, STATES } from 'src/models/settlement/BoxOfficeReportSettlement'
import ModelTable from 'src/modules/admin/components/ModelTable'
import QueryEncodableOptionsMixin from 'src/modules/admin/mixins/QueryEncodableOptionsMixin'
import BoxOfficeReportSettlementBatchDialog from 'src/modules/admin/settlement/components/BoxOfficeReportSettlementBatchDialog'
import BoxOfficeReportSettlementFilter from 'src/modules/admin/settlement/components/BoxOfficeReportSettlementFilter'

export default {
  name: 'BoxOfficeReportSettlementTable',
  components: {
    BoxOfficeReportSettlementBatchDialog,
    BoxOfficeReportSettlementFilter,
    ModelTable
  },
  mixins: [QueryEncodableOptionsMixin],
  configuration: ['currentCircuit', 'currentSite'],
  models: {
    boxOfficeReportSettlements: {
      boxOfficeReport: {
        site: {
          fields: ['id', 'name']
        },
        studio: {
          fields: ['id', 'name']
        },
        movie: {
          fields: ['id', 'name', 'posterImage', 'posterImageSmallParams']
        },
        movieTerm: {
          showingBadges: 'default'
        }
      },
      studioSettlement: true,
      variables () {
        return {
          boxOfficeReportId: this.boxOfficeReportId,
          batched: this.filters.batched,
          states: this.filters.states,
          types: this.filters.types,
          siteIds: _.map(this.filters.sites, 'id'),
          studioIds: _.map(this.filters.studios, 'id'),
          movieIds: _.map(this.filters.movies, 'id'),
          movieTermIds: _.map(this.filters.movieTerms, 'id'),
          studioSettlementId: this.studioSettlement?.id,
          descending: true
        }
      }
    },
    studios: {
      fields: ['id', 'name'],
      variables () {
        return {
          searchString: this.filters.studioSearchString,
          limit: 10
        }
      }
    },
    movies: {
      fields: ['id', 'name'],
      variables () {
        return {
          searchString: this.filters.movieSearchString,
          orderBy: 'createdAt',
          descending: true,
          limit: 10
        }
      }
    },
    movieTerms: {
      fields: ['id'],
      showingBadges: {
        fields: ['id', 'title', 'displayName']
      },
      movie: {
        fields: ['id', 'name']
      },
      variables () {
        return {
          movieIds: this.movie ? [this.movie.id] : _.map(this.filters.movies, 'id'),
          searchString: this.filters.movieTermSearchString,
          limit: 50
        }
      }
    }
  },
  props: {
    boxOfficeReportId: {
      type: Number,
      required: false,
      default: null
    },
    movie: {
      type: Object,
      required: false,
      default: null
    },
    movieTerm: {
      type: Object,
      required: false,
      default: null
    },
    studioSettlement: {
      type: Object,
      required: false,
      default: null
    },
    submodel: {
      type: Boolean,
      required: false,
      default: false
    },
    showControls: {
      type: Boolean,
      required: false,
      default: true
    },
    initialSelection: {
      type: Array,
      required: false,
      default: () => ([])
    },
    columnFn: {
      type: Function,
      required: false,
      default: (columns) => columns
    }
  },
  data () {
    return {
      allowBatch: true,
      filters: {
        batched: null,
        types: [],
        states: [],
        sites: [],
        studios: [],
        movies: [],
        movieTerms: [],
        weeksOf: [],
        disables: [],
        studioSearchString: null,
        movieSearchString: null,
        movieTermSearchString: null
      }
    }
  },
  computed: {
    defaultQueryEncodableOptions () {
      return {
        batched: null,
        types: null,
        states: null
      }
    },
    columns () {
      return _.compact(this.columnFn([
        { field: 'id' },
        { name: 'identifier', field: settlement => ({ label: settlement.identifier, tooltip: settlement.description }), component: 'chip' },
        { name: 'studioSettlement', field: settlement => settlement.studioSettlement, component: 'model' },
        { name: 'state', field: settlement => iconForState(settlement.state), component: 'icon' },
        { name: 'type', field: settlement => _.startCase(settlement.type), component: 'chip' },
        { name: 'amount', field: settlement => settlement.amount, component: 'money' },
        { name: 'site', field: settlement => settlement.boxOfficeReport.site, component: 'model', editDisabled: true, classes: 'sensitive-column' },
        { name: 'studio', field: settlement => settlement.boxOfficeReport.studio, component: 'model' },
        { name: 'movie', field: settlement => settlement.boxOfficeReport.movie, component: 'model', editPath: 'movie-terms' },
        { name: 'movieTerm', field: settlement => settlement.boxOfficeReport.movieTerm, component: 'model', editDisabled: true },
        { name: 'actions', label: 'Action', align: 'right' }
      ]))
    }
  },
  watch: {
    movie: {
      handler () {
        this.updateFilterDisables()
      },
      immediate: true
    },
    movieTerm: {
      handler () {
        this.updateFilterDisables()
      },
      immediate: true
    }
  },
  created () {
    _.extend(this, { STATES })
  },
  methods: {
    decodeUrlParamOption (key, value) {
      switch (key) {
        case 'batched':
          return value || null
        case 'type':
        case 'state':
        case 'weeksOf':
          return value?.split(',') || []
        default:
          return undefined
      }
    },
    initializeQueryEncodableOptions () {
      this.filters.batched = this.currentQueryDecodedOptions.batched
      this.filters.states = this.currentQueryDecodedOptions.states?.split(',') || []
      this.filters.types = this.currentQueryDecodedOptions.types?.split(',') || []
      this.filters.weeksOf = this.currentQueryDecodedOptions.weeksOf?.split(',') || []
    },
    updateFilterDisables () {
      if (!_.isNil(this.movieTerm)) {
        this.filters.disables = ['studios', 'movies', 'movieTerms']
        this.filters.movies = _.compact([this.movie])
        this.filters.movieTerms = _.compact([this.movieTerm])
        if (!this.currentSite.isLibrary) {
          this.filters.sites = _.compact([this.currentSite])
          this.filters.disables.push('sites')
        }
      } else if (!_.isNil(this.movie)) {
        this.filters.disables = ['studios', 'movies']
        this.filters.movies = _.compact([this.movie])
        if (!this.currentSite.isLibrary) {
          this.filters.sites = _.compact([this.currentSite])
          this.filters.disables.push('sites')
        }
      } else {
        this.filters.disables = []
        this.filters.movies = []
        this.filters.movieTerms = []
      }
    },
    async voidAll () {
      const results = await this.boxOfficeReportSettlements.voidAll(_.map(this.boxOfficeReportSettlements.data, 'id'))

      if (!_.isEmpty(results.errors)) {
        this.$mh.showErrors(results.errors)
      }

      if (!_.isNil(results.data?.actionResult)) {
        const actionResult = JSON.parse(results.data.actionResult.value)
        this.$mh.notify(`Updated ${actionResult.box_office_report_settlements_updated} Box Office Report Settlements`)
        this.boxOfficeReportSettlements.fetch(true)
      }
    },
    async settleAll () {
      const results = await this.boxOfficeReportSettlements.settleAll(_.map(this.boxOfficeReportSettlements.data, 'id'))

      if (!_.isEmpty(results.errors)) {
        this.$mh.showErrors(results.errors)
      }

      if (!_.isNil(results.data?.actionResult)) {
        const actionResult = JSON.parse(results.data.actionResult.value)
        this.$mh.notify(`Updated ${actionResult.box_office_report_settlements_updated} Box Office Report Settlements`)
        this.boxOfficeReportSettlements.fetch(true)
      }
    },
    async unsettleAll () {
      const results = await this.boxOfficeReportSettlements.unsettleAll(_.map(this.boxOfficeReportSettlements.data, 'id'))

      if (!_.isEmpty(results.errors)) {
        this.$mh.showErrors(results.errors)
      }

      if (!_.isNil(results.data?.actionResult)) {
        const actionResult = JSON.parse(results.data.actionResult.value)
        this.$mh.notify(`Updated ${actionResult.box_office_report_settlements_updated} Box Office Report Settlements`)
      }
    }
  }
}
</script>
